
import { logger } from '@/utils/logger';
import type { Asset } from '../../types';
import { logBetaAnalyticsEvent } from '@/utils/beta-analytics';
import {
    requireProjectScope,
    type ProjectScope,
} from './project-scope';
import type {
    ScriptJobStatus,
    ScriptJobStatusValue,
} from './script-job-status';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { callBackendCommand, firebaseFunctionCommands, getBackend, getCurrentIdentitySession, requireIdentityToken } from '@/backend';

export type {
    ScriptJobStatus,
    ScriptJobStatusValue,
} from './script-job-status';

type JsonRecord = Record<string, unknown>;

export type GeneratedAsset = {
    id: string;
    prompt: string;
    svgCode: string;
} & JsonRecord;

type ScopedProductionExtractionInput = ProjectScope & {
    sourceJobId?: string;
    sceneIds?: string[];
    dryRun?: boolean;
};

export type ScopedProductionExtractionResult = {
    success: true;
    kind: 'props' | 'soundCues';
    runId: string;
    sourceJobId: string;
    artifactPath: string;
    count: number;
    extracted: number;
    persisted: number;
    dryRun: boolean;
    sourceFile: string;
    sceneIds: string[];
};

export type ScriptRepairResult = {
    success: boolean;
    results: Record<string, unknown>;
};

const isRecord = (value: unknown): value is JsonRecord => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
);

const asString = (value: unknown, fallback = ''): string => (
    typeof value === 'string' ? value : fallback
);

const firstString = (values: unknown[], fallback = ''): string => {
    for (const value of values) {
        if (typeof value === 'string') return value;
    }
    return fallback;
};

const getCallableResult = (value: unknown): unknown => (
    isRecord(value) ? value.result : undefined
);

function toGeneratedAsset(value: unknown, prompt: string): GeneratedAsset {
    const rawData = isRecord(value) ? value : {};

    return {
        ...rawData,
        id: asString(rawData.id, crypto.randomUUID()),
        svgCode: firstString([rawData.svgCode, rawData.svg, rawData.code, rawData.markup]),
        prompt,
    };
}

/**
 * Generates an SVG asset using the Genkit Cloud Function
 */
export async function generateAsset(prompt: string): Promise<GeneratedAsset> {
    try {
        if (!getCurrentIdentitySession()) {
            throw new Error("User must be signed in to generate assets.");
        }
        const result = await callBackendCommand(firebaseFunctionCommands.generateSvgAsset, { prompt });
        return toGeneratedAsset(result, prompt);
    } catch (error: unknown) {
        logger.error("Error generating asset:", error);
        throw error;
    }
}

/**
 * Saves an asset to the User's personal 'custom_assets' collection
 */
export async function saveToUserLibrary(asset: Asset | GeneratedAsset): Promise<boolean> {
    try {
        const userId = getEffectiveUserId();
        if (!userId) throw new Error("User not authenticated");

        logger.sync(`Saving asset to user library for ${userId}`, asset);

        await callBackendCommand(firebaseFunctionCommands.saveUserLibraryAsset, {
            userId,
            asset,
        });
        return true;
    } catch (error: unknown) {
        logger.error("Error saving to user library:", error);
        throw error;
    }
}

/**
 * Subscribe to the User's personal custom assets
 */
export function subscribeToUserAssets(userId: string, onUpdate: (assets: Asset[]) => void): () => void {
    const client = getBackend().userAssets;
    if (!client) {
        logger.warn('UserAssetClient is not configured for the active backend.');
        return () => {};
    }
    return client.subscribeToUserAssets(userId, onUpdate);
}

/**
 * Triggers script analysis on an existing file in the repository.
 * 
 * Routes to one of two pipelines based on scriptTextAccess:
 * - Beta (scriptTextAccess=true): Full OCR pipeline (Mistral OCR → PubSub fan-out → script blocks)
 * - Standard (scriptTextAccess=false): Lightweight metadata-only extraction (single Gemini call)
 * 
 * Called when user selects an existing PDF from the file picker,
 * or after uploading a new PDF via the file picker.
 */
export async function triggerScriptAnalysis(
    scope: ProjectScope,
    storagePath: string,
    options?: {
        scriptTextAccess?: boolean;
        importMode?: 'replace' | 'append';
        forceReOcr?: boolean;
        analyticsSource?: string;
    }
): Promise<{ jobId: string }> {
    if (!getCurrentIdentitySession()) throw new Error("User must be signed in to analyze scripts.");
    
    const useFullPipeline = options?.scriptTextAccess ?? false;
    const command = useFullPipeline
        ? firebaseFunctionCommands.startOcrJob
        : firebaseFunctionCommands.startMetadataExtraction;
    const traceName = useFullPipeline ? 'script_import' : 'metadata_extraction';

    const operation = async () => {
        const payload: {
            orgId: string;
            projectId: string;
            storagePath: string;
            importMode?: 'replace' | 'append';
            forceReOcr?: boolean;
        } = { orgId: scope.orgId, projectId: scope.projectId, storagePath };

        if (options?.importMode) {
            payload.importMode = options.importMode;
        }
        if (options?.forceReOcr !== undefined) {
            payload.forceReOcr = options.forceReOcr;
        }

        const data = await callBackendCommand(command, payload);
        
        if (!data.success) {
            throw new Error(data.message || "Failed to start script analysis");
        }

        logBetaAnalyticsEvent('beta_script_import_started', {
            source: options?.analyticsSource ?? (useFullPipeline ? 'ocr' : 'metadata_only'),
            script_text_access_enabled: useFullPipeline,
        });

        return { jobId: data.jobId };
    };

    const telemetry = getBackend().telemetry;
    return telemetry ? telemetry.trace(traceName, operation) : operation();
}

// DEPRECATED: The old analyzeScriptCloud function has been removed.
// Script analysis now uses the Document AI pipeline:
// - New uploads: file upload session finalization → triggerScriptAnalysis(storagePath) → processOcrResult
// - Re-analysis: triggerScriptAnalysis(projectId, storagePath) → Document AI → processOcrResult

/**
 * Manually extracts props from the latest or selected script import source PDF.
 */
export async function extractPropsFromScriptCloud(
    input: ScopedProductionExtractionInput,
): Promise<ScopedProductionExtractionResult> {
    try {
        if (!getCurrentIdentitySession()) {
            throw new Error("User must be signed in to extract props.");
        }
        
        return callBackendCommand(firebaseFunctionCommands.extractPropsFromScript, input);
    } catch (error: unknown) {
        logger.error("Error extracting props:", error);
        throw error;
    }
}

/**
 * Manually extracts sound cues from the latest or selected script import source PDF.
 */
export async function extractSoundCuesFromScriptCloud(
    input: ScopedProductionExtractionInput,
): Promise<ScopedProductionExtractionResult> {
    try {
        if (!getCurrentIdentitySession()) {
            throw new Error("User must be signed in to extract sound cues.");
        }
        
        return callBackendCommand(firebaseFunctionCommands.extractSoundCuesFromScript, input);
    } catch (error: unknown) {
        logger.error("Error extracting sound cues:", error);
        throw error;
    }
}

// ============================================
// Job-Based Script Analysis (Background Processing)
// ============================================

function toScriptRepairResult(value: unknown): ScriptRepairResult {
    const data = isRecord(value) ? value : {};
    return {
        success: data.success === true,
        results: isRecord(data.results) ? data.results : {},
    };
}


/**
 * Get the status of a script analysis job.
 * Returns the current status, progress message, and result path if complete.
 */
export async function getScriptJobStatus(scope: ProjectScope, jobId: string): Promise<ScriptJobStatus | null> {
    try {
        const client = getBackend().scriptJobs;
        if (!client) {
            throw new Error('ScriptJobClient is not configured for the active backend.');
        }
        return await client.getScriptJobStatus(scope, jobId);
    } catch (error: unknown) {
        logger.error("Error getting job status:", error);
        throw error;
    }
}

/**
 * Convert job status to percentage for progress display.
 */
export function getJobProgressPercent(status: ScriptJobStatus['status']): number {
    switch (status) {
        case 'queued': return 0;
        case 'processing': return 10;
        case 'stage_1': return 25;
        case 'stage_2': return 75;
        case 'partial': return 90;  // Partial completion
        case 'repairing': return 95;  // Repair in progress
        case 'completed': return 100;
        case 'failed': return 0;
        default: return 0;
    }
}

/**
 * Repair a partial script import by re-running failed batches
 * and/or re-extracting Phase 2 data (structure, props, soundCues).
 * 
 * @param phases - Optional array: 'blocks' | 'structure' | 'props' | 'soundCues'
 *   Defaults to ['blocks'] if omitted.
 */
export async function repairScriptImport(
    scope: ProjectScope,
    jobId: string,
    phases?: ('blocks' | 'structure' | 'props' | 'soundCues')[]
): Promise<ScriptRepairResult> {
    try {
        const checkedScope = requireProjectScope(scope);

        const idToken = await requireIdentityToken("User must be signed in to repair script imports.");
        const response = await fetch('/api/repairScriptImport', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ data: { ...checkedScope, jobId, phases } })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`repairScriptImport failed (${response.status}): ${errorText}`);
        }

        const json: unknown = await response.json();
        return toScriptRepairResult(getCallableResult(json));
    } catch (error: unknown) {
        logger.error("Error repairing script import:", error);
        throw error;
    }
}

/**
 * Subscribe to script job updates for the current user in a project.
 * Uses Firestore real-time listener for instant notifications.
 * Returns unsubscribe function.
 */
export function subscribeToScriptJob(
    scope: ProjectScope,
    onUpdate: (status: ScriptJobStatusValue, error?: string, job?: ScriptJobStatus & { jobId?: string }) => void,
    jobId?: string,
    sourceFile?: string
): () => void {
    const client = getBackend().scriptJobs;
    if (!client) {
        logger.error('ScriptJobClient is not configured for the active backend.');
        return () => {};
    }
    return client.subscribeToScriptJob(scope, onUpdate, jobId, sourceFile);
}

/**
 * Subscribe to the latest script job for a project, returning full job data.
 * Used by useScriptJobStatus hook for the toolbar status chip.
 */
export function subscribeToLatestScriptJob(
    scope: ProjectScope,
    onUpdate: (job: ScriptJobStatus & { jobId: string } | null) => void,
    userIdOverride?: string
): () => void {
    const client = getBackend().scriptJobs;
    if (!client) {
        logger.error('ScriptJobClient is not configured for the active backend.');
        return () => {};
    }
    return client.subscribeToLatestScriptJob(scope, onUpdate, userIdOverride);
}
